// src/pages/agent-booking-system-sabre-integration/components/ResultsGrid.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ResultsGrid = ({ results, onBookingSelect, selectedBooking, userProfile }) => {
  const [activeTab, setActiveTab] = useState('flights');
  const [sortBy, setSortBy] = useState('price');
  const [sortOrder, setSortOrder] = useState('asc');

  const tabs = [
    { id: 'flights', label: 'Flights', count: results?.flights?.length || 0 },
    { id: 'hotels', label: 'Hotels', count: results?.hotels?.length || 0 },
    { id: 'cars', label: 'Cars', count: results?.cars?.length || 0 }
  ];

  const sortOptions = {
    flights: [
      { value: 'price', label: 'Price' },
      { value: 'duration', label: 'Duration' },
      { value: 'departure_time', label: 'Departure Time' },
      { value: 'ranking_score', label: 'Recommended' }
    ],
    hotels: [
      { value: 'price', label: 'Price' },
      { value: 'star_rating', label: 'Star Rating' },
      { value: 'distance', label: 'Distance' }
    ],
    cars: [
      { value: 'price', label: 'Price' },
      { value: 'category', label: 'Category' }
    ]
  };

  const getSortedResults = (data, type) => {
    if (!data || !Array.isArray(data)) return [];
    
    return [...data]?.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'price':
          aValue = type === 'flights' ? a?.fare_amount : a?.rates?.[0]?.amount || 0;
          bValue = type === 'flights' ? b?.fare_amount : b?.rates?.[0]?.amount || 0;
          break;
        case 'duration':
          aValue = new Date(a.arrival_time) - new Date(a.departure_time);
          bValue = new Date(b.arrival_time) - new Date(b.departure_time);
          break;
        case 'departure_time':
          aValue = new Date(a.departure_time);
          bValue = new Date(b.departure_time);
          break;
        case 'star_rating':
          aValue = a?.star_rating || 0;
          bValue = b?.star_rating || 0;
          break;
        case 'ranking_score':
          aValue = a?.ranking_score || 0;
          bValue = b?.ranking_score || 0;
          break;
        default:
          return 0;
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };

  const formatDuration = (departureTime, arrivalTime) => {
    const diff = new Date(arrivalTime) - new Date(departureTime);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime)?.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleBooking = (item, type) => {
    const bookingData = {
      id: item?.id,
      type: type,
      item: item,
      sabre_data: item?.sabre_data || {}
    };
    onBookingSelect?.(bookingData);
  };

  const renderFlightResult = (flight) => (
    <div 
      key={flight?.id}
      className="bg-surface border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <span className="text-primary-700 font-heading-bold text-lg">
              {flight?.airline_code}
            </span>
          </div>
          <div>
            <div className="flex items-center space-x-2 mb-1">
              <span className="font-heading-semibold text-text-primary">
                {flight?.flight_number}
              </span>
              {flight?.ranking_score && (
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-sm text-text-secondary">
                    {flight?.ranking_score?.toFixed(1)}
                  </span>
                </div>
              )}
            </div>
            <p className="text-sm text-text-secondary">
              {flight?.aircraft_type} • {flight?.booking_class} Class
            </p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-heading-bold text-text-primary mb-1">
            ${flight?.fare_amount?.toLocaleString()}
          </div>
          <div className="text-sm text-text-secondary">
            per person
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-lg font-heading-semibold text-text-primary">
            {formatTime(flight?.departure_time)}
          </div>
          <div className="text-sm text-text-secondary">
            {flight?.origin_airport}
          </div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-2 h-2 bg-border rounded-full"></div>
            <div className="flex-1 h-px bg-border"></div>
            <Icon name="Plane" size={16} className="text-text-secondary" />
            <div className="flex-1 h-px bg-border"></div>
            <div className="w-2 h-2 bg-border rounded-full"></div>
          </div>
          <div className="text-sm text-text-secondary">
            {formatDuration(flight?.departure_time, flight?.arrival_time)}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-lg font-heading-semibold text-text-primary">
            {formatTime(flight?.arrival_time)}
          </div>
          <div className="text-sm text-text-secondary">
            {flight?.destination_airport}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="Users" size={14} />
            <span>{flight?.availability_count} seats left</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Leaf" size={14} className="text-green-600" />
            <span>{flight?.carbon_footprint || 'Low'} CO₂</span>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button className="px-4 py-2 text-sm font-body-medium text-text-secondary border border-border rounded-lg hover:bg-secondary-50 transition-colors">
            Details
          </button>
          <button 
            onClick={() => handleBooking(flight, 'flight')}
            className="px-4 py-2 text-sm font-body-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Book Flight
          </button>
        </div>
      </div>
    </div>
  );

  const renderHotelResult = (hotel) => (
    <div 
      key={hotel?.id}
      className="bg-surface border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-heading-semibold text-text-primary">
              {hotel?.hotel_name}
            </h3>
            <div className="flex items-center">
              {[...Array(hotel?.star_rating || 0)]?.map((_, i) => (
                <Icon key={i} name="Star" size={14} className="text-yellow-500 fill-yellow-500" />
              ))}
            </div>
          </div>
          <p className="text-sm text-text-secondary mb-2">
            {hotel?.address}
          </p>
          <div className="flex items-center space-x-4 text-sm text-text-secondary">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={14} />
              <span>{hotel?.location_code}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Wifi" size={14} />
              <span>{hotel?.amenities?.length || 0} amenities</span>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-heading-bold text-text-primary mb-1">
            ${hotel?.rates?.[0]?.amount?.toLocaleString() || 299}
          </div>
          <div className="text-sm text-text-secondary">
            per night
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          {hotel?.amenities?.slice(0, 4)?.map((amenity) => (
            <span 
              key={amenity}
              className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full"
            >
              {amenity?.replace('_', ' ')}
            </span>
          ))}
          {hotel?.amenities?.length > 4 && (
            <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full">
              +{hotel?.amenities?.length - 4} more
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="CheckCircle" size={14} className="text-green-600" />
            <span>Free cancellation</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="CreditCard" size={14} />
            <span>Pay at hotel</span>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button className="px-4 py-2 text-sm font-body-medium text-text-secondary border border-border rounded-lg hover:bg-secondary-50 transition-colors">
            View Details
          </button>
          <button 
            onClick={() => handleBooking(hotel, 'hotel')}
            className="px-4 py-2 text-sm font-body-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Book Hotel
          </button>
        </div>
      </div>
    </div>
  );

  const renderEmptyState = (type) => (
    <div className="text-center py-12">
      <Icon 
        name={type === 'flights' ? 'Plane' : type === 'hotels' ? 'Building' : 'Car'} 
        size={48} 
        className="text-text-secondary mx-auto mb-4" 
      />
      <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
        No {type} found
      </h3>
      <p className="text-text-secondary">
        Try adjusting your search parameters or search different dates.
      </p>
    </div>
  );

  const currentResults = results?.[activeTab] || [];
  const sortedResults = getSortedResults(currentResults, activeTab);

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="border-b border-border bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading-bold text-text-primary">
            Search Results
          </h2>
          
          {currentResults?.length > 0 && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-text-secondary">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="px-3 py-1 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {sortOptions?.[activeTab]?.map(option => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-lg transition-colors"
              >
                <Icon 
                  name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                  size={16} 
                />
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-body-medium rounded-lg transition-colors ${
                activeTab === tab?.id
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' :'text-text-secondary hover:bg-secondary-50 hover:text-text-primary'
              }`}
            >
              <span>{tab?.label}</span>
              {tab?.count > 0 && (
                <span className="px-1.5 py-0.5 bg-text-secondary bg-opacity-20 text-xs rounded-full">
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Results */}
      <div className="flex-1 overflow-auto p-6">
        {sortedResults?.length > 0 ? (
          <div className="space-y-4">
            {sortedResults?.map((item) => (
              activeTab === 'flights' ? renderFlightResult(item) :
              activeTab === 'hotels' ? renderHotelResult(item) :
              null
            ))}
          </div>
        ) : (
          renderEmptyState(activeTab)
        )}
      </div>
    </div>
  );
};

export default ResultsGrid;